import { NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { hide, shutdown } from '@intercom/messenger-js-sdk';
import { User } from 'avn/models/user';
import { AuthAppService } from 'avn/services/auth-app/auth-app.service';
import { Constants } from 'avn/services/constants';
import { UserService } from 'avn/services/user.service';
import { SignInBaseComponent } from 'avn/shared/components/sign-in-base/sign-in-base';

import { WebsiteUiService } from '../../website-ui.service';
import { HOW_IT_WORKS_LINKS } from '../../website.data';
import { NavLink } from '../../website.models';

// Same provider log in as HeaderComponent (form, user loading, redirects, logout) with the v4b layout
@Component({
  selector: 'app-web-nav',
  imports: [NgTemplateOutlet, RouterLink, ReactiveFormsModule],
  templateUrl: './web-nav.html',
  styleUrls: ['./web-nav.scss']
})
export class WebNavComponent extends SignInBaseComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthAppService);
  private userApi = inject(UserService);
  private ui = inject(WebsiteUiService);

  howItWorksLinks = HOW_IT_WORKS_LINKS;
  mainLinks: NavLink[] = [
    { label: "Who it's for", link: '/who-its-for' },
    { label: 'Network', link: '/network' },
    { label: 'Evidence', link: '/evidence' },
    { label: 'About Avaana', link: '/about' }
  ];

  showHowItWorks = false;
  showMobileMenu = false;
  readonly showLoginDropdown = signal(false);
  readonly isUserLoggedIn = signal(false);
  readonly loggedUser = signal<any>({});
  readonly hasBusiness = signal(false);
  readonly isWellbeingOwner = signal(false);

  override ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.authService.loggedUser.subscribe((userData: any) => {
        this.isUserLoggedIn.set(!!userData?.email);
        if (this.isUserLoggedIn()) {
          this.loggedUser.set(userData);
          this.hasBusiness.set(userData?.businesses?.length > 0 || false);
          this.isWellbeingOwner.set(userData?.is_wellbeing_owner || userData?.is_wellbeing_employees || false);
        }
      }),
      this.ui.openLogin$.subscribe(() => this.openLogin())
    );
  }

  toggleHowItWorks(event: Event): void {
    event.stopPropagation();
    this.closeLoginDropdown();
    this.showHowItWorks = !this.showHowItWorks;
  }

  toggleMobileMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }

  closeMenus(): void {
    this.showHowItWorks = false;
    this.showMobileMenu = false;
    this.closeLoginDropdown();
  }

  bookDemo(): void {
    this.closeMenus();
    this.ui.openContactModal();
  }

  openLogin(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.showLoginDropdown.set(true);
  }

  toggleLoginDropdown(event?: Event): void {
    event?.stopPropagation();
    this.showLoginDropdown.set(!this.showLoginDropdown());
  }

  keepDropdownOpen(event: Event): void {
    event.stopPropagation();
  }

  closeLoginDropdown(): void {
    this.showLoginDropdown.set(false);
  }

  // Angular keeps only one host listener per event, so a single handler closes both popovers
  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closePopovers(): void {
    this.showHowItWorks = false;
    this.closeLoginDropdown();
  }

  protected override promptDelayMs(): number {
    return 0;
  }

  fetchBusinessRouting() {
    const businesses = this.loggedUser()?.businesses;
    const business_id = this.authService.getBusinessUUID() || businesses[0]?.uuid;
    if (business_id) {
      return [`/business/${business_id}`];
    }
    return [''];
  }

  getWellbeingLocation() {
    return `${Constants.WELLBEING_DASHBOARD_URL}?r=${
      this.loggedUser().is_wellbeing_owner ? 'employer' : 'employee'
    }&s=${this.authService.getToken()}`;
  }

  logoutUser(): void {
    this.userApi.logout().subscribe({
      next: () => {
        this.showLoginDropdown.set(false);
        this.isUserLoggedIn.set(false);
        this.loggedUser.set({});
        this.hasBusiness.set(false);
        this.isWellbeingOwner.set(false);
        this.authService.purgeAuth();
        this.authService.setloggedUser(new User());
        this.router.navigate(['/']);
        hide();
        shutdown();
      }
    });
  }
}
